import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "hooks/useToast";
import { Routes } from "router/routes";
import { setPendingRole } from "utils/pendingHiringIntent";
import { ArrowRightNew } from "assets/website";
import { SendIconFilled, VideoCircleIcon } from "assets/icons";
import { motion, AnimatePresence } from "framer-motion";

const CYCLING_WORDS = [
  { text: "Hiring", color: null },
  { text: "Auditing Your Team", color: "#E9750E" },
  { text: "Validating Competence", color: "#007BFF" },
];

interface HeroSectionProps {
  onTalkToTeam: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onTalkToTeam }) => {
  const navigate = useNavigate();
  const { openToast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [wordIndex, setWordIndex] = useState(0);
  const [inputValue, setInputValue] = useState("");

  const isLoggedIn = Boolean(
    localStorage.getItem("precisionAccess") &&
    localStorage.getItem("precisionAccess") !== "undefined"
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((i) => (i + 1) % CYCLING_WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleTryFree = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      openToast({ show: true, heading: "Input required", text: "Please describe what you want to assess.", type: "error", timeOut: 4000 });
      return;
    }
    if (trimmed.length < 5) {
      openToast({ show: true, heading: "Too short", text: "Please enter at least 5 characters.", type: "error", timeOut: 4000 });
      return;
    }
    // Carry the role into the Hiring Assistant once the user finishes onboarding.
    setPendingRole(trimmed);
    // Signed-out visitors sign up (and can switch to "Sign in" from there — the
    // intent survives either route); a visitor who still has a session goes
    // straight to the assistant, where the pick-up modal greets them.
    navigate(isLoggedIn ? Routes.publishAssessment : Routes.signup);
  };

  return (
    <div className="relative mx-auto flex flex-col items-center text-center bg-[#EFF4FA] pt-[140px] md:pt-[200px] pb-16 px-4 md:px-6">
      {/* Dot pattern background */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, #D0D5DD 3px, transparent 3px)`,
          backgroundSize: "30px 30px",
        }}
      />

      {/* Headline */}
      <h1
        data-aos="fade-up"
        data-aos-delay="100"
        className="text-[28px] md:text-5xl lg:text-[52px] font-display font-normal text-[#00003F] lg:leading-[1.1] md:leading-[52px] leading-[36px] mb-4 mx-auto max-w-[900px]"
      >
        Your Complete Assessment Tool for{" "}
        <span className="relative inline-block font-bold">
          {/* fixed spacer — always sized to the longest word so the container never resizes during crossfade */}
          <span className="invisible whitespace-nowrap" aria-hidden>
            Validating Competence
          </span>
          <AnimatePresence>
            <motion.span
              key={wordIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
              style={{ color: CYCLING_WORDS[wordIndex].color ?? undefined }}
            >
              {CYCLING_WORDS[wordIndex].text}
            </motion.span>
          </AnimatePresence>
        </span>
      </h1>

      {/* Subheadline */}
      <p
        data-aos="fade-up"
        data-aos-delay="200"
        className="text-sm md:text-base lg:text-lg text-[#475467] max-w-[740px] mx-auto mb-8 px-2 md:px-0 leading-relaxed"
      >
        Precision assesses competence, behaviour, and mindset so you know who
        to hire, who to develop, and where your team is leaking performance.
      </p>

      {/* CTA Buttons */}
      <div
        data-aos="fade-up"
        data-aos-delay="300"
        className="z-[9] flex flex-col sm:flex-row items-center justify-center gap-3 mb-10 w-full sm:w-auto"
      >
        <button
          onClick={handleTryFree}
          className="px-7 py-3.5 bg-[#012169] text-sm hover:bg-[#011550] text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg shadow-[0px_4px_8px_0px_#01216940] w-full sm:w-auto"
        >
          Try Precision for Free
        </button>
        <button
          onClick={onTalkToTeam}
          className="px-8 py-2.5 bg-white text-base border-[1.5px] border-[#012169] text-[#012169] hover:text-[#011550] font-semibold rounded-lg transition-all duration-200 flex items-center justify-center gap-1 shadow-[0px_4px_8px_0px_#01216940] hover:shadow-[0px_6px_12px_0px_#01216960] w-full sm:w-auto"
        >
          Book a demo
          <VideoCircleIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Assessment Input Widget */}
      <div
        data-aos="fade-up"
        data-aos-delay="400"
        className="relative w-full max-w-[640px] mx-auto bg-white rounded-[12px] border border-[#98A2B3] overflow-hidden"
      >
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="What do you want to assess today?"
          className="w-full h-[140px] p-5 text-sm text-[#344054] placeholder-[#98A2B3] resize-none focus:outline-none bg-transparent leading-relaxed"
        />
        <div className="flex justify-end px-4 pb-4">
          <SendIconFilled
            onClick={handleTryFree}
            className="w-8 h-8 cursor-pointer [&_path]:fill-[#012169]"
          />
        </div>
      </div>
    </div>
  );
};

export { HeroSection };
